export interface DirectionsTarget {
  lat?: number;
  lng?: number;
  /** Entity name — used as a last-resort destination query. */
  label?: string | null;
  /** Street address — used when there are no coordinates. */
  address?: string | null;
}

/**
 * Google Maps directions URL. Prefers exact coordinates, falls back to a street
 * address, then the entity name; null when there is nothing to route to.
 *
 * Returned as a URL (rather than opened) so callers can render a real
 * `<a href>` — see LocationMapCard.
 */
export function directionsUrl({ lat, lng, label, address }: DirectionsTarget): string | null {
  let destination: string | null = null;
  if (typeof lat === 'number' && typeof lng === 'number') {
    destination = `${lat},${lng}`;
  } else if (address && address.trim()) {
    destination = address.trim();
  } else if (label && label.trim()) {
    destination = label.trim();
  }
  if (!destination) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
}

/**
 * Build a keyless Google Maps URL that opens the store's actual place page
 * (business details) from its Google Maps place ID. Falls back to a coordinate
 * or name search when no place ID is present. No API key is required to use it.
 */
export function mapsPlaceUrl(
  placeId?: string | null,
  opts: { location?: string | null; label?: string | null } = {},
): string | null {
  const pid = placeId?.trim();
  const loc = opts.location?.trim();
  const label = opts.label?.trim();
  const query = encodeURIComponent(label || loc || 'place');
  if (pid) {
    return `https://www.google.com/maps/search/?api=1&query=${query}&query_place_id=${encodeURIComponent(pid)}`;
  }
  if (loc) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc)}`;
  return null;
}
